import React, { useState } from "react";
import { X } from "lucide-react";
import { getButtonIcon } from "../lib/buttonIcons";

export default function PromoCard({ promo, lang = "pt", backendUrl, preview = false, anchorStyle = null, inline = false }) {
  const [closed, setClosed] = useState(false);
  if (!promo || promo.enabled === false) return null;
  if (closed && !preview) return null;

  const p = promo;
  const tx = (pt, en) => (lang === "en" ? (en || pt) : pt);
  const Icon = getButtonIcon(p.icon_name || "cart", null);
  const iconBox = p.icon_size ?? 62;
  const iconRadius = Math.round(iconBox * 0.26);
  const iconGlyph = Math.round(iconBox * 0.52);
  const iconImg = Math.round(iconBox * 0.58);

  const title1 = tx(p.title1_pt, p.title1_en);
  const title2 = tx(p.title2_pt, p.title2_en);
  const badge = tx(p.badge_pt, p.badge_en);
  const subtitle = tx(p.subtitle_pt, p.subtitle_en);
  const btnLabel = tx(p.btn_pt, p.btn_en);

  const SHADOWS = {
    none: "none",
    soft: "0 10px 25px rgba(0,0,0,0.25)",
    medium: "0 20px 45px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.12)",
    strong: "0 28px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.18)",
  };
  const cardBorder = p.border_enabled
    ? `${p.border_width ?? 3}px ${p.border_style || "solid"} ${p.border_color || "#FFD23F"}`
    : "1px solid rgba(255,255,255,0.08)";
  const cardShadow = SHADOWS[p.shadow_strength || "medium"] ?? SHADOWS.medium;

  const badgeSize = Number(p.badge_size) || 17;
  const badgeShape = p.badge_shape || "pill";
  const BADGE_SHAPES = {
    pill: { borderRadius: "999px", padX: "16px" },
    rounded: { borderRadius: "12px", padX: "16px" },
    rect: { borderRadius: "4px", padX: "16px" },
    ribbon: { borderRadius: "3px", padX: "22px", clipPath: "polygon(10px 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0 50%)" },
    burst: { borderRadius: "0", padX: "26px", padY: "12px", clipPath: "polygon(50% 0%, 61% 7%, 73% 3%, 78% 15%, 91% 16%, 90% 29%, 100% 38%, 93% 50%, 100% 62%, 90% 71%, 91% 84%, 78% 85%, 73% 97%, 61% 93%, 50% 100%, 39% 93%, 27% 97%, 22% 85%, 9% 84%, 10% 71%, 0% 62%, 7% 50%, 0% 38%, 10% 29%, 9% 16%, 22% 15%, 27% 3%, 39% 7%)" },
    tag: { borderRadius: "4px", padX: "24px", clipPath: "polygon(14px 0, 100% 0, 100% 100%, 14px 100%, 0 50%)" },
    banner: { borderRadius: "3px", padX: "24px", clipPath: "polygon(0 0, 100% 0, calc(100% - 14px) 50%, 100% 100%, 0 100%)" },
    chevron: { borderRadius: "3px", padX: "26px", clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)" },
    shield: { borderRadius: "3px", padX: "20px", padY: "9px", clipPath: "polygon(0 0, 100% 0, 100% 60%, 50% 100%, 0 60%)" },
    diamond: { borderRadius: "0", padX: "32px", padY: "14px", clipPath: "polygon(50% 0, 100% 50%, 50% 100%, 0 50%)" },
    hexagon: { borderRadius: "0", padX: "30px", padY: "10px", clipPath: "polygon(25% 0, 75% 0, 100% 50%, 75% 100%, 25% 100%, 0 50%)" },
    octagon: { borderRadius: "0", padX: "26px", padY: "10px", clipPath: "polygon(20% 0, 80% 0, 100% 30%, 100% 70%, 80% 100%, 20% 100%, 0 70%, 0 30%)" },
    parallelogram: { borderRadius: "3px", padX: "26px", clipPath: "polygon(14% 0, 100% 0, 86% 100%, 0 100%)" },
    cut_corners: { borderRadius: "0", padX: "20px", padY: "9px", clipPath: "polygon(10px 0, calc(100% - 10px) 0, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px), 0 10px)" },
    double_chevron: { borderRadius: "3px", padX: "28px", clipPath: "polygon(0 50%, 14px 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 14px 100%)" },
    bookmark: { borderRadius: "4px 4px 0 0", padX: "20px", padY: "10px", clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%)" },
    star4: { borderRadius: "0", padX: "30px", padY: "16px", clipPath: "polygon(50% 0, 63% 37%, 100% 50%, 63% 63%, 50% 100%, 37% 63%, 0 50%, 37% 37%)" },
  };
  const bShape = BADGE_SHAPES[badgeShape] || BADGE_SHAPES.pill;

  // Badge effects
  const badgeFx = p.badge_effect || "none";
  const BADGE_FX = {
    none: undefined,
    glow: `drop-shadow(0 0 5px ${p.badge_bg || "#E91E8C"}) drop-shadow(0 0 12px ${p.badge_bg || "#E91E8C"})`,
    shadow: "drop-shadow(0 6px 7px rgba(0,0,0,0.55))",
    sticker: "drop-shadow(2px 0 0 #fff) drop-shadow(-2px 0 0 #fff) drop-shadow(0 2px 0 #fff) drop-shadow(0 -2px 0 #fff)",
  };
  const badgeFilter = BADGE_FX[badgeFx];
  const badgeFxClass = badgeFx === "shine" ? "promo-fx-shine" : "";
  const badgeWrapClass = badgeFx === "pulse" ? "promo-fx-pulse" : "";

  // Divider line ("linha anexa") between title and badge
  const divStyle = p.divider_style || "line";
  const divColor = p.divider_color || "rgba(255,255,255,0.18)";
  const divThk = p.divider_thickness ?? 1;
  const divTilt = p.divider_tilt ?? 0;
  let dividerCss = { width: "100%", height: `${divThk}px` };
  let dividerClass = "";
  if (divStyle === "line") dividerCss.background = divColor;
  else if (divStyle === "glow") { dividerCss.background = divColor; dividerCss.boxShadow = `0 0 8px ${divColor}, 0 0 14px ${divColor}`; dividerCss.borderRadius = "999px"; }
  else if (divStyle === "glow_pulse") { dividerCss.background = divColor; dividerCss.borderRadius = "999px"; dividerClass = "promo-divider-pulse"; dividerCss["--div-color"] = divColor; }
  else if (divStyle === "gradient") dividerCss.background = `linear-gradient(90deg, transparent, ${divColor}, transparent)`;
  else if (divStyle === "rainbow") { dividerCss.background = "linear-gradient(90deg,#FF5757,#FFBD59,#FFE74C,#4CD964,#4CC9F0,#7B61FF,#FF5DA2)"; dividerCss.borderRadius = "999px"; dividerCss.height = `${Math.max(2, divThk)}px`; }
  else if (divStyle === "shadow") { dividerCss.background = divColor; dividerCss.boxShadow = "0 3px 5px rgba(0,0,0,0.45)"; dividerCss.borderRadius = "999px"; }
  else if (divStyle === "double") dividerCss = { width: "100%", height: `${Math.max(5, divThk * 3)}px`, borderTop: `${divThk}px solid ${divColor}`, borderBottom: `${divThk}px solid ${divColor}` };
  else if (divStyle === "dots_large") dividerCss = { width: "100%", height: 0, borderTop: `${Math.max(4, divThk * 2)}px dotted ${divColor}` };
  else if (divStyle === "dashed" || divStyle === "dotted") dividerCss = { width: "100%", height: 0, borderTop: `${Math.max(2, divThk)}px ${divStyle} ${divColor}` };
  if (divTilt) dividerCss = { ...dividerCss, transform: `rotate(${divTilt}deg)` };
  const VIEWS = {
    flat: undefined,
    persp_left: "perspective(800px) rotateY(14deg)",
    persp_right: "perspective(800px) rotateY(-14deg)",
    persp_left_strong: "perspective(700px) rotateY(26deg)",
    persp_right_strong: "perspective(700px) rotateY(-26deg)",
    tilt_up: "perspective(800px) rotateX(12deg)",
    tilt_down: "perspective(800px) rotateX(-12deg)",
    iso: "perspective(900px) rotateX(8deg) rotateY(-12deg)",
    iso_right: "perspective(900px) rotateX(8deg) rotateY(12deg)",
    corner_tl: "perspective(900px) rotateX(10deg) rotateY(14deg)",
    corner_tr: "perspective(900px) rotateX(10deg) rotateY(-14deg)",
    corner_bl: "perspective(900px) rotateX(-10deg) rotateY(14deg)",
    corner_br: "perspective(900px) rotateX(-10deg) rotateY(-14deg)",
    book_left: "perspective(600px) rotateY(38deg)",
    book_right: "perspective(600px) rotateY(-38deg)",
    float: "perspective(1200px) rotateX(5deg)",
  };
  const cardTransform = VIEWS[p.card_view || "flat"];
  const cardFx = p.card_effect || "none";
  const cardFxClass = cardFx !== "none" ? `promo-card-fx-${cardFx}` : "";

  const card = (
    <div
      className="relative flex flex-col items-stretch shadow-2xl"
      data-testid="promo-card"
      style={{
        width: inline ? "100%" : `${p.width || 260}px`,
        maxWidth: inline ? `${(p.width || 260) + 40}px` : "calc(100vw - 24px)",
        background: `linear-gradient(180deg, ${p.card_bg1 || "#5B2A86"} 0%, ${p.card_bg2 || "#2E1248"} 100%)`,
        borderRadius: `${p.card_radius ?? 28}px`,
        padding: "20px 18px 30px",
        boxShadow: cardShadow,
        border: cardBorder,
        transform: cardTransform,
        transformStyle: cardTransform ? "preserve-3d" : undefined,
      }}
    >
      {cardFx === "shimmer" && (
        <div className="promo-shimmer-overlay" style={{ borderRadius: `${p.card_radius ?? 28}px` }} aria-hidden="true" />
      )}
      {p.dismissible !== false && !preview && (
        <button
          onClick={() => setClosed(true)}
          data-testid="promo-card-close"
          className="absolute -top-2 -right-2 h-7 w-7 inline-flex items-center justify-center rounded-full bg-white text-[#3D1A5B] shadow-lg hover:scale-110 transition-transform"
        >
          <X size={15} strokeWidth={3} />
        </button>
      )}

      {/* Header: icon + title */}
      <div className="flex items-start gap-3">
        {p.icon_enabled !== false && (
        <div className="relative shrink-0" style={{ marginTop: p.show_awning ? "13px" : 0 }}>
          {p.show_awning && (
            <>
              <div
                className="absolute left-1/2 -translate-x-1/2 z-10"
                style={{
                  top: "0px",
                  width: `${iconBox}px`,
                  height: "17px",
                  borderRadius: "13px 13px 0 0",
                  background: "repeating-linear-gradient(90deg, #D7263D 0 10px, #FBF7F2 10px 20px)",
                  WebkitMaskImage: "radial-gradient(circle at 9px 100%, transparent 0 7px, #000 8px)",
                  WebkitMaskSize: "18px 100%",
                  WebkitMaskRepeat: "repeat-x",
                  maskImage: "radial-gradient(circle at 9px 100%, transparent 0 7px, #000 8px)",
                  maskSize: "18px 100%",
                  maskRepeat: "repeat-x",
                  filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.25))",
                }}
              />
              <div
                className="absolute left-1/2 -translate-x-1/2 z-20"
                style={{
                  top: "-5px",
                  width: `${iconBox - 2}px`,
                  height: "11px",
                  background: "#FFFFFF",
                  borderRadius: "999px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
                }}
              />
            </>
          )}
          <div
            className="flex items-center justify-center"
            style={{
              width: `${iconBox}px`, height: `${iconBox}px`,
              borderRadius: `${iconRadius}px`,
              background: `linear-gradient(180deg, ${p.icon_bg || "#FFC107"} 0%, #F4A700 100%)`,
              boxShadow: "0 6px 14px rgba(0,0,0,0.35), inset 0 2px 2px rgba(255,255,255,0.5)",
            }}
          >
            {p.icon_type === "image" && p.icon_image ? (
              <img src={`${backendUrl}/api/files/${p.icon_image}`} alt="" style={{ width: `${iconImg}px`, height: `${iconImg}px`, objectFit: "contain" }} />
            ) : (
              Icon && <Icon size={iconGlyph} color={p.icon_color || "#3D1A5B"} strokeWidth={2.4} />
            )}
          </div>
        </div>
        )}

        {p.title_enabled !== false && (
        <div className={`flex flex-col leading-none pt-1 flex-1 ${p.title_align === "center" ? "items-center text-center" : p.title_align === "right" ? "items-end text-right" : "items-start text-left"} font-${p.title_font || "luckiest"}`} style={{ transform: p.title_tilt ? `rotate(${p.title_tilt}deg)` : undefined, transformOrigin: "center" }}>
          {title1 && (
            <span className="font-extrabold tracking-wide" style={{ color: p.title1_color || "#FFFFFF", fontSize: `${p.title_size ?? 30}px`, textShadow: "1px 1px 0 rgba(0,0,0,0.35), 0 2px 0 rgba(0,0,0,0.25)" }}>
              {title1}
            </span>
          )}
          {title2 && (
            <span className="font-extrabold tracking-wide" style={{ color: p.title2_color || "#FFD23F", fontSize: `${p.title_size ?? 30}px`, textShadow: "1px 1px 0 rgba(0,0,0,0.35), 0 2px 0 rgba(0,0,0,0.25)" }}>
              {title2}
            </span>
          )}
        </div>
        )}
      </div>

      {p.divider_enabled !== false && divStyle !== "none" && (
        <div className={`mt-3 mb-3 ${dividerClass}`} style={dividerCss} />
      )}

      {p.badge_enabled !== false && badge && (
        <div className={`flex justify-center ${badgeWrapClass}`}>
          <span
            data-testid="promo-card-badge"
            className={`inline-block font-bold text-center font-${p.badge_font || "baloo"} ${badgeFxClass}`}
            style={{
              background: p.badge_bg || "#E91E8C",
              color: p.badge_color || "#FFFFFF",
              fontSize: `${badgeSize}px`,
              borderRadius: bShape.borderRadius,
              clipPath: bShape.clipPath,
              paddingLeft: bShape.padX,
              paddingRight: bShape.padX,
              paddingTop: bShape.padY || "6px",
              paddingBottom: bShape.padY || "6px",
              boxShadow: bShape.clipPath ? "none" : "0 5px 12px rgba(0,0,0,0.28), inset 0 2px 2px rgba(255,255,255,0.3)",
              filter: badgeFilter,
              transform: p.badge_tilt ? `rotate(${p.badge_tilt}deg)` : undefined,
            }}
          >
            {badge}
          </span>
        </div>
      )}

      {p.subtitle_enabled !== false && subtitle && (
        <p
          className={`mt-3 text-center font-bold uppercase leading-tight font-${p.subtitle_font || "baloo"}`}
          style={{ color: p.subtitle_color || "#FFFFFF", fontSize: `${p.subtitle_size || 12}px`, letterSpacing: "0.04em", transform: p.subtitle_tilt ? `rotate(${p.subtitle_tilt}deg)` : undefined }}
        >
          {subtitle}
        </p>
      )}

      {p.btn_enabled !== false && btnLabel && (
        <a
          href={p.btn_link || "#"}
          target={p.btn_link && /^https?:\/\//.test(p.btn_link) ? "_blank" : undefined}
          rel="noopener noreferrer"
          data-testid="promo-card-cta"
          className={`text-center font-extrabold uppercase tracking-wide font-${p.btn_font || "luckiest"}`}
          style={{
            background: `linear-gradient(180deg, ${p.btn_bg || "#FFC107"} 0%, #F4A700 100%)`,
            color: p.btn_color || "#C1121F",
            borderRadius: "999px",
            padding: "11px 22px",
            fontSize: "16px",
            boxShadow: "0 7px 16px rgba(0,0,0,0.35), inset 0 2px 2px rgba(255,255,255,0.5)",
            position: "absolute",
            left: "50%",
            bottom: "0px",
            transform: `translate(-50%, 50%)${p.btn_tilt ? ` rotate(${p.btn_tilt}deg)` : ""}`,
            whiteSpace: "nowrap",
            zIndex: 2,
          }}
        >
          {btnLabel}
        </a>
      )}
    </div>
  );

  if (preview) return cardFxClass ? <div className={cardFxClass}>{card}</div> : card;
  if (inline) return <div className="animate-[promoIn_0.6s_ease]" style={{ marginLeft: `${p.shift_x ?? 0}px`, marginTop: `${p.space_top ?? 20}px` }} data-testid="promo-card-wrap"><div className={cardFxClass}>{card}</div></div>;

  return (
    <div
      className="z-40 animate-[promoIn_0.6s_ease]"
      style={anchorStyle || { position: "fixed", bottom: `${p.offset_y ?? 24}px`, left: `${p.offset_x ?? 24}px` }}
      data-testid="promo-card-wrap"
    >
      <div className={cardFxClass}>{card}</div>
    </div>
  );
}
